import { composeSettingItemConfigByIdMap } from './composeSettingItemConfigByIdMap.ts'
import {
  type FlattenedSettingValues,
  safeParseItemValue,
  type SettingItemConfigId,
  type SettingsConfig,
} from './settingsConfigSchema.ts'

export type SettingPersistentStorage = typeof window.localStorage
export type SettingPersistentStorageKey = Parameters<
  SettingPersistentStorage['setItem']
>[0]

/**
 * Compose a key for setting storage.
 *
 * At first, it includes `settingAccountId`, a user unique id
 * to separate settings of different users.
 * It was removed later on for two reasons.
 *
 * 1. Dark theme is applied in the root app level, even without auth.
 *    - If we were to use `settingAccountId` unauthed route
 *      will always have a default theme and flash to dark theme later,
 *      which is not a good experience.
 * 2. In most cases, our users don't share their computer,
 *    so `settingAccountId` wouldn't be useful.
 *
 * The only consideration is, what should we do if we needed
 * to store these settings in database.
 * Maybe we should do local-first, then sync it similar to vscode.
 */
export const composeSettingStorageKey = (
  settingsId: SettingsConfig['settingsId'],
) => {
  return `_settings:${settingsId}`
}

export const readSettingStorage = <RootConfig extends SettingsConfig>(
  key: SettingPersistentStorageKey,
  settingsConfig: RootConfig,
): FlattenedSettingValues<RootConfig> => {
  const serializedData = window.localStorage.getItem(key)
  if (!serializedData) return {}

  let parsedData: Record<string, unknown>
  try {
    parsedData = JSON.parse(serializedData)
  } catch (error) {
    console.error(
      `[readSettingStorage]: Error parsing settings from storage for key ${key}:`,
      error,
    )
    return {}
  }

  const itemConfigByIdMap = composeSettingItemConfigByIdMap(settingsConfig)
  const values: Record<SettingItemConfigId, unknown> = {}

  Object.entries(parsedData).forEach(([itemConfigId, value]) => {
    const itemConfig = itemConfigByIdMap[itemConfigId]

    // If the itemKey from storage is stale or invalid
    // (the config doesn't exist anymore), ignore it.
    // Only parse value from the valid key according to the current config.
    if (itemConfig) {
      const parsedValue = safeParseItemValue(itemConfig, value)
      values[itemConfigId] = parsedValue

      // Validate if the value is one of the options.
      // Otherwise, set the value to `undefined`,
      // so we can fallback to default value.
      if (itemConfig.type === 'string' && Array.isArray(itemConfig.options)) {
        const isValueValid = itemConfig.options.some(
          (option) => option.value === parsedValue,
        )
        if (!isValueValid) {
          values[itemConfigId] = undefined
        }
      }
    }
  })

  return values as FlattenedSettingValues<RootConfig>
}

export const writeSettingStorage = (
  key: SettingPersistentStorageKey,
  values: Record<string, unknown>,
) => {
  window.localStorage.setItem(key, JSON.stringify(values))
}
